import React, { useEffect, useMemo } from 'react';

import { TEXTS } from 'application/pages/texts';
import PlayerProvider, { usePlayerContext } from 'application/providers/PlayerProvider';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import { useThematicBlockRandomMediafiles } from '@entities/media';

import InfiniteMediaList from 'application/components/MediaList/InfiniteMediaList';
import SkeletonItem from 'application/components/MediaList/MediaListItemSkeleton/SkeletonItem';
import AnalyticsService, { ParamNameAMP } from 'application/services/AnalyticsService';
import { ThematicEventNameAMP } from 'application/services/AnalyticsService/ampTypes';
import { NY_BANNER_TYPES_ENUM } from 'application/services/AnalyticsService/eventTypes/thematic';
import styles from './index.module.scss';

export type CustomContentPreviewModalProps = {
  thematicContentBlockId: string;
  onCancel: () => void;
  onSubmit: () => void;
};

const CustomContentPreviewModalContent = ({
  thematicContentBlockId,
  onCancel,
  onSubmit,
}: CustomContentPreviewModalProps) => {
  const playerContext = usePlayerContext();

  const thematicBlockRandomMediafiles = useThematicBlockRandomMediafiles();

  const mediafiles = thematicBlockRandomMediafiles.mediafiles;

  const currentMediafiles = useMemo(
    () => mediafiles?.slice(0, mediafiles.length - (mediafiles.length % 2)),
    [mediafiles]
  );

  const loadRandomMediafiles = () => {
    if (playerContext.isPlaying) {
      playerContext.stop();
    }
    thematicBlockRandomMediafiles.request(
      currentMediafiles.map((mediafile) => mediafile.id),
      thematicContentBlockId
    );
  };

  const handleCancel = () => {
    if (playerContext.isPlaying) {
      playerContext.stop();
    }
    onCancel();
  };

  const handleSubmit = () => {
    AnalyticsService.event(ThematicEventNameAMP.ADD_THEMATIC_CONTENT_PRESSED, {
      [ParamNameAMP.TYPE]: NY_BANNER_TYPES_ENUM.MODAL_NEW_YEAR_TRACKS,
    });

    if (playerContext.isPlaying) {
      playerContext.stop();
    }
    onSubmit();
  };

  useEffect(() => {
    loadRandomMediafiles();
  }, []);

  const onLoadMoreRandomMediafiles = () => {
    const excludeIds = thematicBlockRandomMediafiles.mediafiles?.map(({ id }) => id);
    thematicBlockRandomMediafiles.loadMore(thematicContentBlockId, excludeIds);
  };

  return (
    <ModalScreen
      title={TEXTS.CUSTOM_CONTENT_PREVIEW_MODAL_TITLE}
      onClose={handleCancel}
      containerActionButtonsClassName={styles.CustomContentPreviewModalButtonsContaiener}
      actionButtons={[
        <Button
          key="button_preview_custom_content_apply"
          elementId="button_preview_custom_content_apply"
          type={ButtonType.PRIMARY}
          onClick={handleSubmit}>
          {TEXTS.CUSTOM_CONTENT_PREVIEW_MODAL_BUTTON_APPLY}
        </Button>,
      ]}>
      <InfiniteMediaList
        className={styles.CustomContentPreviewModalList}
        mediafiles={currentMediafiles || []}
        streamId={thematicContentBlockId}
        withDislikes={false}
        hasNextPage={thematicBlockRandomMediafiles.paginationInfo?.hasNextPage ?? false}
        totalItemsCount={thematicBlockRandomMediafiles.paginationInfo?.totalCount}
        loadMore={onLoadMoreRandomMediafiles}
        itemListSkeleton={<SkeletonItem withPerformerTitle />}
      />
    </ModalScreen>
  );
};

const CustomContentPreviewModal = ({ thematicContentBlockId, onCancel, onSubmit }: CustomContentPreviewModalProps) => (
  <PlayerProvider>
    <CustomContentPreviewModalContent
      thematicContentBlockId={thematicContentBlockId}
      onCancel={onCancel}
      onSubmit={onSubmit}
    />
  </PlayerProvider>
);

export default CustomContentPreviewModal;
